import './table.css';
import './table.rebrand.css';

function buildCell(column: Element, isHeader: boolean) {
	// Custom logic to allow specifying colspan and rowspan from content
	let colSpan: number | undefined;
	let rowSpan: number | undefined;
	[...column.children].forEach((child) => {
		if (child.innerHTML.toLowerCase().startsWith('colspan=')) {
			colSpan = Number(child.innerHTML.split('=')[1]);
			child.remove();
		}
		if (child.innerHTML.toLowerCase().startsWith('rowspan=')) {
			rowSpan = Number(child.innerHTML.split('=')[1]);
			child.remove();
		}
	});
	const cell = isHeader
		? document.createElement('th')
		: document.createElement('td');
	if (colSpan) cell.colSpan = colSpan;
	if (rowSpan) cell.rowSpan = rowSpan;
	return cell;
}

export default function decorate(block: HTMLElement): void {
	const table = document.createElement('table');
	const thead = document.createElement('thead');
	const tbody = document.createElement('tbody');
	const rows: HTMLTableRowElement[] = [];

	const header = !block.classList.contains('no-header');
	const isHighlightFirstRow = block.classList.contains('highlight-first-row');
	const isHighlightFirst2Rows = block.classList.contains(
		'highlight-first-2-rows',
	);
	const isHighlightFirstCol = block.classList.contains('highlight-first-col');
	const isHighlightFirst2Cols = block.classList.contains(
		'highlight-first-2-cols',
	);

	table.appendChild(tbody);

	[...block.children].forEach((child, rowIndex) => {
		const row = document.createElement('tr');
		const cells: Array<'th' | HTMLTableCellElement> = [];
		[...(child as HTMLElement).children].forEach((col, colIndex) => {
			const isFirstCol = colIndex === 0;
			const isFirst2Cols = colIndex === 0 || colIndex === 1;

			let isTheadRow: boolean;

			const hasComboHighlight =
				(isHighlightFirstCol && isHighlightFirst2Rows) ||
				(isHighlightFirst2Cols && isHighlightFirst2Rows) ||
				(isHighlightFirstCol && isHighlightFirstRow) ||
				(isHighlightFirst2Cols && isHighlightFirstRow);

			if (hasComboHighlight) {
				isTheadRow =
					header &&
					((!isHighlightFirst2Rows && rowIndex === 0) ||
						(isHighlightFirst2Rows && rowIndex < 2));
			} else {
				isTheadRow =
					header &&
					!isHighlightFirstCol &&
					!isHighlightFirst2Cols &&
					((!isHighlightFirst2Rows && rowIndex === 0) ||
						(isHighlightFirst2Rows && rowIndex < 2));
			}

			const useTh =
				isTheadRow ||
				(isHighlightFirstCol && isFirstCol && !isTheadRow) ||
				(isHighlightFirst2Cols && isFirst2Cols && !isTheadRow);

			const cell = buildCell(col, useTh);
			if (useTh) {
				if (isTheadRow) {
					cell.setAttribute('scope', 'col');
				} else if (isHighlightFirstCol && isFirstCol) {
					cell.setAttribute('scope', 'row');
				} else if (isHighlightFirst2Cols && isFirst2Cols) {
					cell.setAttribute('scope', 'row');
				}
			}

			if (col.children.length > 1) {
				cell.classList.add('table__row-multiple-content');
				col.classList.add('table__cell-multiple-content');

				const links = col.querySelectorAll('a');
				if (links.length > 0) {
					links.forEach((link) => {
						link.classList.add('small');
					});
				}
			}
			cell.append(col);
			cells.push(cell);
		});

		row.append(...cells);

		if (
			header &&
			((!isHighlightFirst2Rows &&
				!isHighlightFirstCol &&
				!isHighlightFirst2Cols &&
				rowIndex === 0) ||
				(isHighlightFirstRow && rowIndex === 0) ||
				(isHighlightFirst2Rows && rowIndex < 2))
		) {
			thead.appendChild(row);
		} else {
			tbody.appendChild(row);
		}

		rows.push(row);
	});

	while (block.firstChild) {
		block.removeChild(block.firstChild);
	}
	if (thead.children.length > 0) {
		table.insertBefore(thead, tbody);
	}
	block.appendChild(table);

	if (!block.classList.contains('glossary')) {
		mergeRows(rows);
		mergeColumns(rows);
	}

	rows.forEach((row) => {
		Array.from(row.querySelectorAll('td')).forEach((tdElement) => {
			if (tdElement.textContent?.includes('checkmark')) {
				tdElement.classList.add('checkmark');
				tdElement.textContent = '';
				const checkmarkLabel = document.createElement('span');
				checkmarkLabel.classList.add('sr-only');
				checkmarkLabel.textContent = 'checkmark';
				tdElement.appendChild(checkmarkLabel);
			}
		});
	});

	if (block.classList.contains('glossary')) {
		const links = block.querySelectorAll('a');
		links.forEach((link) => {
			link.setAttribute('target', '_blank');
			link.setAttribute('rel', 'noopener noreferrer');
		});
	}
}

function mergeRows(rows: HTMLTableRowElement[]): void {
	const rowLength = rows.length;
	const colsLength = rows[0].children.length;

	for (let colIndex = 0; colIndex < colsLength; colIndex++) {
		let prevRowspan = 0;

		for (let rowIndex = 0; rowIndex < rowLength; rowIndex++) {
			const currentCell = rows[rowIndex].children[colIndex];

			if (isEmptyCell(currentCell as HTMLElement)) {
				continue;
			}

			let rowSpan = 1;
			let previousCell;
			if (colIndex > 0) {
				previousCell = rows[rowIndex].children[colIndex - 1];
				if (previousCell.hasAttribute('rowspan')) {
					prevRowspan = parseInt(previousCell.getAttribute('rowspan') || '0');
				}
			}

			for (let k = rowIndex + 1; k < rowLength; k++) {
				const nextCell = rows[k].children[colIndex];

				let nextRowPrevCell;
				if (k < rowLength - 1 && colIndex > 0) {
					nextRowPrevCell = rows[k].children[colIndex - 1];
				}

				if (
					isEmptyCell(nextCell as HTMLElement) &&
					(prevRowspan === 0 ||
						(prevRowspan - rowSpan > 1 &&
							nextRowPrevCell &&
							isEmptyCell(nextRowPrevCell as HTMLElement)))
				) {
					rowSpan++;
					if (nextCell instanceof HTMLElement) {
						nextCell.style.display = 'none';
					}
				} else {
					break;
				}
			}

			if (rowSpan > 1) {
				currentCell.setAttribute('rowspan', rowSpan.toString());
			}

			rowIndex += rowSpan - 1;
		}
	}
}

function mergeColumns(rows: HTMLTableRowElement[]): void {
	const numberOfRows = rows.length;

	for (let rowIndex = 0; rowIndex < numberOfRows; rowIndex++) {
		const cells = rows[rowIndex].children;
		const numberOfCells = cells.length;

		for (let cellIndex = 0; cellIndex < numberOfCells; cellIndex++) {
			const currentCell = cells[cellIndex];
			let rowSpan = 0;
			if (rowIndex > 0 && cellIndex < numberOfCells - 1) {
				rowSpan = parseInt(
					rows[rowIndex - 1].children[cellIndex + 1].getAttribute('rowspan') ||
						'0',
				);
			}

			if (isEmptyCell(currentCell as HTMLElement)) {
				continue;
			}

			let colSpan = 1;

			for (
				let nextCellIndex = cellIndex + 1;
				nextCellIndex < numberOfCells;
				nextCellIndex++
			) {
				const nextCell = cells[nextCellIndex];

				if (isEmptyCell(nextCell as HTMLElement)) {
					colSpan++;
				} else {
					break;
				}
			}

			if (colSpan > 1 && rowSpan === 0) {
				currentCell.setAttribute('colspan', colSpan.toString());

				for (
					let hiddenCellIndex = cellIndex + 1;
					hiddenCellIndex < cellIndex + colSpan;
					hiddenCellIndex++
				) {
					if (cells[hiddenCellIndex] instanceof HTMLElement) {
						(cells[hiddenCellIndex] as HTMLElement).style.display = 'none';
					}
				}

				cellIndex += colSpan - 1;
			}
		}
	}
}

function isEmptyCell(cell: HTMLElement | null | undefined): boolean {
	return cell instanceof HTMLElement && cell.textContent?.trim() === '';
}
